import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { OUTPUT_LANGUAGES, type OutputLanguage } from '../types';
import { colors, formes, radius, spacing, typography } from '../theme/tokens';

export interface OutputLanguagePickerProps {
  value: OutputLanguage;
  onChange: (value: OutputLanguage) => void;
  label: string;
  hint?: string;
  // 'compact' : bascule « FR | EN » d'en-tête (écrans refaits). Le libellé devient l'étiquette d'accessibilité.
  variante?: 'puces' | 'compact';
  testID: string;
}

/**
 * Sélecteur de la langue du RÉSULTAT. À ne pas confondre avec LanguagePicker, qui saisit les
 * langues PARLÉES du candidat pour la rubrique correspondante du CV.
 *
 * Il n'existe volontairement aucun sélecteur de langue d'ENTRÉE : le modèle détecte sans effort la
 * langue d'un CV ou d'une annonce entière, et un champ déclaratif ne servirait qu'à le contredire.
 * Le découplage entrée/sortie est obtenu par une consigne de prompt, pas par un champ de plus.
 */
export function OutputLanguagePicker({ value, onChange, label, hint, variante = 'puces', testID }: OutputLanguagePickerProps) {
  if (variante === 'compact') {
    return (
      <View testID={testID} accessibilityRole="radiogroup" accessibilityLabel={label} style={styles.piste}>
        {OUTPUT_LANGUAGES.map((option) => {
          const active = option.value === value;
          return (
            <Pressable
              key={option.value}
              testID={`${testID}-${option.value}`}
              accessibilityRole="button"
              accessibilityLabel={option.label}
              accessibilityState={{ selected: active }}
              onPress={() => onChange(option.value)}
              style={[styles.bascule, active && styles.basculeActive]}
            >
              <Text style={[styles.basculeTexte, active && styles.basculeTexteActif]}>{option.value.toUpperCase()}</Text>
            </Pressable>
          );
        })}
      </View>
    );
  }
  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.chips}>
        {OUTPUT_LANGUAGES.map((option) => {
          const active = option.value === value;
          return (
            <Pressable
              key={option.value}
              testID={`${testID}-${option.value}`}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              onPress={() => onChange(option.value)}
              style={[styles.chip, active && styles.chipActive]}
            >
              <Text style={[styles.chipText, active && styles.chipTextActive]}>{option.label}</Text>
            </Pressable>
          );
        })}
      </View>
      {hint ? <Text style={styles.hint}>{hint}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  piste: { flexDirection: 'row', gap: 4, padding: 3, borderRadius: formes.rayon.md, backgroundColor: colors.grey200 },
  // Cible de 44 pt : la maquette dessine 30 de haut, trop bas pour un pouce.
  bascule: { minWidth: formes.cible, minHeight: formes.cible - 6, borderRadius: formes.rayon.sm, alignItems: 'center', justifyContent: 'center', paddingHorizontal: spacing.sm },
  basculeActive: { backgroundColor: colors.night },
  basculeTexte: { fontSize: 12.5, fontWeight: '700', color: colors.grey500 },
  basculeTexteActif: { fontWeight: '800', color: colors.white },
  container: { gap: spacing.xs },
  label: { fontSize: typography.small.size, fontWeight: '700', color: colors.grey700 },
  chips: { flexDirection: 'row', gap: spacing.sm },
  chip: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.pill,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey200,
  },
  chipActive: { backgroundColor: colors.violet, borderColor: colors.violet },
  chipText: { fontSize: typography.small.size, color: colors.grey700 },
  chipTextActive: { color: colors.white, fontWeight: '700' },
  hint: { fontSize: typography.small.size, color: colors.grey500 },
});
